import { useEffect, useState, useCallback } from "react";
import { ChevronLeft, ChevronRight, X } from "lucide-react";
import { Dialog, DialogContent } from "./ui/dialog";
import { AuthImage } from "./FileUploader";

/**
 * Minimal full-screen image zoomer for the various places we display photo
 * thumbnails (Tasks before/after photos, Checklist run items, etc.).
 *
 * Unlike the Photos page's `PhotoLightbox` — which is metadata-heavy (caption
 * + tags + delete) — this is a pure visual zoom: full-bleed dark backdrop,
 * the source image at its native size, optional prev/next when the caller
 * passes a list of sibling paths. Close on X, Escape key, or backdrop click.
 *
 * Usage:
 *   const [lb, setLb] = useState(null);
 *   <img onClick={() => setLb({ paths: ["a.jpg", "b.jpg"], index: 0 })} />
 *   <ImageLightbox state={lb} onClose={() => setLb(null)} />
 *
 * The `state` prop is `null` (closed) or `{ paths, index, alt? }`.
 */
export default function ImageLightbox({ state, onClose }) {
  const [index, setIndex] = useState(state?.index || 0);

  // Keep our local nav cursor in sync whenever the caller opens a new
  // batch of photos (e.g. user clicks a different thumbnail).
  useEffect(() => {
    if (state) setIndex(state.index || 0);
  }, [state]);

  // Keyboard nav: arrow keys cycle through, Escape closes. We bind to
  // document so the keys work even when focus lives on the backdrop.
  const onKey = useCallback((e) => {
    if (!state) return;
    if (e.key === "Escape") { e.preventDefault(); onClose(); }
    if (!state.paths || state.paths.length < 2) return;
    if (e.key === "ArrowRight") { e.preventDefault(); setIndex((i) => (i + 1) % state.paths.length); }
    if (e.key === "ArrowLeft")  { e.preventDefault(); setIndex((i) => (i - 1 + state.paths.length) % state.paths.length); }
  }, [state, onClose]);

  useEffect(() => {
    if (!state) return;
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [state, onKey]);

  if (!state) return null;
  const paths = state.paths || [];
  const total = paths.length;
  const path = paths[index];
  const showNav = total > 1;

  return (
    <Dialog open onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent
        className="w-[100vw] h-[100dvh] max-w-none p-0 bg-zinc-950/95 border-0 rounded-none sm:rounded-none flex items-center justify-center overflow-hidden"
        data-testid="image-lightbox"
      >
        {/* Custom close button — DialogContent renders its own X but we
            recolor a new one absolute-positioned over the dark backdrop. */}
        <button
          type="button"
          onClick={onClose}
          className="absolute right-3 top-3 z-30 rounded-full bg-black/60 hover:bg-black/80 text-white p-2"
          aria-label="Close"
          data-testid="image-lightbox-close"
        >
          <X className="h-5 w-5" />
        </button>

        {showNav && (
          <>
            <button
              type="button"
              onClick={() => setIndex((i) => (i - 1 + total) % total)}
              className="absolute left-3 top-1/2 -translate-y-1/2 z-20 rounded-full bg-black/60 hover:bg-black/80 text-white p-2"
              aria-label="Previous"
              data-testid="image-lightbox-prev"
            >
              <ChevronLeft className="h-5 w-5" />
            </button>
            <button
              type="button"
              onClick={() => setIndex((i) => (i + 1) % total)}
              className="absolute right-3 top-1/2 -translate-y-1/2 z-20 rounded-full bg-black/60 hover:bg-black/80 text-white p-2"
              aria-label="Next"
              data-testid="image-lightbox-next"
            >
              <ChevronRight className="h-5 w-5" />
            </button>
            <div
              className="absolute bottom-4 left-1/2 -translate-x-1/2 z-20 rounded-full bg-black/60 text-white text-xs px-3 py-1 font-mono"
              data-testid="image-lightbox-counter"
            >
              {index + 1} / {total}
            </div>
          </>
        )}

        <AuthImage
          path={path}
          alt={state.alt || ""}
          className="max-h-[100dvh] max-w-[100vw] w-auto h-auto object-contain"
        />
      </DialogContent>
    </Dialog>
  );
}
